import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGame, clearGameState } from "./hooks/useGame";
import { useSwipeNav } from "./hooks/useSwipeNav";
import { useTheme } from "./hooks/useTheme";
import { useLocalProgress } from "./hooks/useLocalProgress";
import { LAUNCH_DATE } from "@griddle/generator";
import { RANDOM_PUZZLE_KEY, puzzleKey, getUtcToday, addDays } from "./lib/constants";
import { Header } from "./components/Header";
import { Grid } from "./components/Grid";
import { SwapCounter } from "./components/SwapCounter";
import { WinModal } from "./components/WinModal";
import { Archive } from "./components/Archive";
import { LoseModal } from "./components/LoseModal";
import { LoadingGrid } from "./components/LoadingGrid";
import { SolvedBadge } from "./components/SolvedBadge";
import { Landing } from "./components/Landing";
import { Footer } from "./components/Footer";
import { Privacy } from "./components/Privacy";
import { Stats } from "./components/Stats";
import { Leaderboard } from "./components/Leaderboard";
import { StreakToast } from "./components/StreakToast";
import { ChangeLog, hasUnseenChangelog, markChangelogSeen } from "./components/ChangeLog";
import { fetchPuzzle, fetchRandomPuzzle, fetchLeaderboard } from "./lib/api";
import { Puzzle, Difficulty } from "./types";
import "./App.css";

// --- Random puzzle persistence ---

type GridSize = "7x7" | "9x9";

function saveRandomPuzzleToStorage(size: GridSize, puzzle: Puzzle): void {
  try {
    const all = JSON.parse(localStorage.getItem(RANDOM_PUZZLE_KEY) || "{}");
    all[size] = puzzle;
    localStorage.setItem(RANDOM_PUZZLE_KEY, JSON.stringify(all));
  } catch { /* localStorage unavailable */ }
}

function loadRandomPuzzleFromStorage(size: GridSize): Puzzle | null {
  try {
    const all = JSON.parse(localStorage.getItem(RANDOM_PUZZLE_KEY) || "{}");
    return all[size] ?? null;
  } catch {
    return null;
  }
}

// --- URL helpers ---

type View = "landing" | "game" | "archive" | "stats" | "leaderboard" | "privacy";

interface RouteState {
  view: View;
  size: GridSize;
  date: string;
}

function parseRoute(): RouteState {
  const path = window.location.pathname;
  const today = getUtcToday();

  const archiveMatch = path.match(/^\/(7x7|9x9)\/archive$/);
  if (archiveMatch) {
    return { view: "archive", size: archiveMatch[1] as GridSize, date: today };
  }

  const statsMatch = path.match(/^\/(7x7|9x9)\/stats$/);
  if (statsMatch) {
    return { view: "stats", size: statsMatch[1] as GridSize, date: today };
  }

  const leaderboardMatch = path.match(/^\/(7x7|9x9)\/leaderboard$/);
  if (leaderboardMatch) {
    return { view: "leaderboard", size: leaderboardMatch[1] as GridSize, date: today };
  }

  // Legacy URL support
  if (path === "/stats") {
    return { view: "stats" as View, size: "7x7" as GridSize, date: today };
  }
  if (path === "/archive") {
    const params = new URLSearchParams(window.location.search);
    const size = params.get("size") === "9x9" ? "9x9" : "7x7";
    return { view: "archive", size, date: today };
  }

  const latestMatch = path.match(/^\/(7x7|9x9)\/latest$/);
  if (latestMatch) {
    return { view: "game", size: latestMatch[1] as GridSize, date: today };
  }

  const match = path.match(/^\/(7x7|9x9)\/(random|\d{4}-\d{2}-\d{2})$/);
  if (match) {
    return { view: "game", size: match[1] as GridSize, date: match[2] };
  }

  const sizeOnly = path.match(/^\/(7x7|9x9)$/);
  if (sizeOnly) {
    return { view: "game", size: sizeOnly[1] as GridSize, date: today };
  }

  if (path === "/privacy") {
    return { view: "privacy", size: "7x7", date: today };
  }

  // Root path or unknown — show landing page
  if (path === "/" || path === "") {
    return { view: "landing", size: "7x7", date: today };
  }

  // Unknown path — default to today's 7x7
  return { view: "game", size: "7x7", date: today };
}

function buildUrl(state: RouteState): string {
  if (state.view === "archive") return `/${state.size}/archive`;
  if (state.view === "stats") return `/${state.size}/stats`;
  if (state.view === "leaderboard") return `/${state.size}/leaderboard`;
  // Use "latest" in the URL for today's date so bookmarks stay current
  if (state.date !== "random" && state.date === getUtcToday()) {
    return `/${state.size}/latest`;
  }
  return `/${state.size}/${state.date}`;
}

function pushRoute(state: RouteState) {
  const url = buildUrl(state);
  if (window.location.pathname + window.location.search !== url) {
    window.history.pushState(null, "", url);
  }
}

function replaceRoute(state: RouteState) {
  window.history.replaceState(null, "", buildUrl(state));
}

// --- Date navigation ---

// LAUNCH_DATE imported from @griddle/generator

// --- Components ---

function findUnsolved(from: string, direction: 1 | -1, size: string, isSolved: (d: string, s: string) => boolean): string | null {
  const today = getUtcToday();
  let date = addDays(from, direction);
  // Limit search to avoid infinite loop (max ~2 years)
  for (let i = 0; i < 730; i++) {
    if (date < LAUNCH_DATE || date > today) return null;
    if (!isSolved(date, size)) return date;
    date = addDays(date, direction);
  }
  return null;
}

function GameView({
  puzzle,
  onRetry,
  onMarkSolved,
  onStats,
  onLeaderboard,
  nextAction,
  onNavigate,
  streakBadgeRef,
}: {
  puzzle: Puzzle;
  onRetry: () => void;
  onMarkSolved: (date: string, size: string, swapsUsed: number, maxSwaps: number, elapsedMs: number, focusedMs: number) => void;
  onStats: () => void;
  onLeaderboard: (params?: { view: "time" | "stars" | "winrate" | "streak"; date?: string }) => void;
  nextAction: { label: string; onClick: () => void };
  onNavigate: (date: string) => void;
  streakBadgeRef: React.RefObject<HTMLButtonElement | null>;
}) {
  const {
    cellStates, swapsUsed, swapsRemaining, isWon, isLost, startedAt, solvedAt, getLiveFocusedMs,
    winSeen, loseSeen, markWinSeen, markLoseSeen, handleCellClick, handleSwap, isCellSwappable,
  } = useGame(puzzle);
  const { isSolved, getResult, getStats, getPlayStreak, invalidateStreaks, waitForSync } = useLocalProgress();
  const priorAttempts = useMemo(() =>
    puzzle.date !== "random" ? (getResult(puzzle.date, puzzle.size)?.attempts ?? 0) : 0,
    [puzzle.date, puzzle.size, getResult]
  );

  const [showWin, setShowWin] = useState(false);
  const [showLose, setShowLose] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [timeRank, setTimeRank] = useState<number | null>(null);
  const toastShownRef = useRef(false);
  // Capture streak at mount (before any completion this session)
  const [streakBefore] = useState(() =>
    puzzle.date !== "random" ? getPlayStreak(puzzle.size).current : null
  );

  const today = getUtcToday();
  const isDaily = puzzle.date !== "random";
  const onPrev = isDaily && puzzle.date > LAUNCH_DATE
    ? () => onNavigate(addDays(puzzle.date, -1))
    : undefined;
  const onNext = isDaily && puzzle.date < today
    ? () => onNavigate(addDays(puzzle.date, 1))
    : undefined;

  // Swipe anywhere in the game view (not just on the board) to change days.
  // The Grid handles swipes over the board itself (with flick-vs-drag) and stops
  // pointer propagation, so this only fires for the surrounding/blank areas.
  const containerSwipe = useSwipeNav((dir) => { if (dir === "next") onNext?.(); else onPrev?.(); });

  const prevUnsolved = useMemo(() =>
    isDaily ? findUnsolved(puzzle.date, -1, puzzle.size, isSolved) : null,
    [isDaily, puzzle.date, puzzle.size, isSolved]
  );
  const nextUnsolved = useMemo(() =>
    isDaily ? findUnsolved(puzzle.date, 1, puzzle.size, isSolved) : null,
    [isDaily, puzzle.date, puzzle.size, isSolved]
  );

  // Show win modal — only on first win (winSeen is false)
  useEffect(() => {
    if (isWon && !winSeen && !showWin) {
      const timer = setTimeout(() => {
        if (puzzle.date !== "random") {
          const elapsed = (solvedAt ?? Date.now()) - startedAt;
          onMarkSolved(puzzle.date, puzzle.size, swapsUsed, puzzle.maxSwaps, elapsed, getLiveFocusedMs());
        }
        if (!toastShownRef.current) { setShowToast(true); toastShownRef.current = true; }
        setShowWin(true);
        markWinSeen();
      }, 500);
      return () => clearTimeout(timer);
    }
  // Note: startedAt/solvedAt are stable once win occurs (solvedAt is frozen)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWon, winSeen, showWin, markWinSeen, onMarkSolved, puzzle.date, swapsUsed, puzzle.maxSwaps, puzzle.size]);

  // Fetch time leaderboard rank whenever win modal opens
  useEffect(() => {
    if (!showWin || puzzle.date === "random") return;
    let cancelled = false;
    (async () => {
      try {
        await waitForSync();
        const res = await fetchLeaderboard(puzzle.size, "time", puzzle.date, true);
        if (!cancelled) setTimeRank(res.userRank);
      } catch { /* non-critical */ }
    })();
    return () => { cancelled = true; };
  }, [showWin, puzzle.size, puzzle.date, waitForSync]);

  // Show lose modal — only on first loss (loseSeen is false)
  useEffect(() => {
    if (isLost && !loseSeen && !showLose) {
      setShowToast(true);
      toastShownRef.current = true;
      setShowLose(true);
      markLoseSeen();
      setTimeout(() => invalidateStreaks(), 100);
    }
  }, [isLost, loseSeen, showLose, markLoseSeen, invalidateStreaks]);

  return (
    <>
      <main className="game-container" {...containerSwipe}>
        <div className="status-row">
          <div className="date-nav-group">
            <button className={`date-nav date-nav-skip${prevUnsolved ? "" : " date-nav-hidden"}`} onClick={prevUnsolved ? () => onNavigate(prevUnsolved) : undefined} aria-label="Previous unsolved puzzle">«</button>
            <button className={`date-nav${onPrev ? "" : " date-nav-hidden"}`} onClick={onPrev} aria-label="Previous puzzle">‹</button>
          </div>
          <div className="status-content">
            {isWon && (
              <SolvedBadge
                swapsRemaining={swapsRemaining}
                getLiveFocusedMs={getLiveFocusedMs}
                solvedAt={solvedAt}
                onClick={() => setShowWin(true)}
              />
            )}
            {!isWon && (
              <SwapCounter
                swapsUsed={swapsUsed}
                swapsRemaining={swapsRemaining}
                maxSwaps={puzzle.maxSwaps}
                attempt={priorAttempts + 1}
              />
            )}
          </div>
          <div className="date-nav-group">
            <button className={`date-nav${onNext ? "" : " date-nav-hidden"}`} onClick={onNext} aria-label="Next puzzle">›</button>
            <button className={`date-nav date-nav-skip${nextUnsolved ? "" : " date-nav-hidden"}`} onClick={nextUnsolved ? () => onNavigate(nextUnsolved) : undefined} aria-label="Next unsolved puzzle">»</button>
          </div>
        </div>
        <div className="reset-link-row">
          {!isWon && puzzle.date !== "random" && swapsUsed > 0 && (
            <button
              className="reset-link"
              onClick={() => {
                if (window.confirm("Reset this puzzle? This counts as a failed attempt.")) {
                  onRetry();
                }
              }}
            >
              Reset puzzle
            </button>
          )}
        </div>
        {!isWon && (
          <>
          </>
        )}
        <Grid
          cellStates={cellStates}
          onCellClick={handleCellClick}
          onSwap={handleSwap}
          isCellSwappable={isCellSwappable}
          onSwipe={(dir) => { if (dir === "next") onNext?.(); else onPrev?.(); }}
        />
      </main>
      {streakBefore !== null && showToast && (
        <StreakToast from={streakBefore} to={getPlayStreak(puzzle.size).current} badgeRef={streakBadgeRef} />
      )}
      {showWin && (() => {
        const sizeStats = getStats(puzzle.size);
        return (
          <WinModal
            swapsUsed={swapsUsed}
            swapsRemaining={swapsRemaining}
            maxSwaps={puzzle.maxSwaps}
            startedAt={startedAt}
            solvedAt={solvedAt}
            focusedMs={getLiveFocusedMs()}
            date={puzzle.date}
            size={puzzle.size}
            cellStates={cellStates}
            winStreak={sizeStats.currentWinStreak}
            longestWinStreak={sizeStats.longestWinStreak}
            onClose={() => { setShowWin(false); setShowToast(false); }}
            onRetry={swapsRemaining < 0 && puzzle.date !== "random" ? onRetry : undefined}
            onStats={onStats}
            onLeaderboard={() => onLeaderboard(puzzle.date !== "random" ? { view: "time" as const, date: puzzle.date } : undefined)}
            timeRank={timeRank}
            onPrevUnsolved={prevUnsolved ? () => onNavigate(prevUnsolved) : undefined}
            onNextUnsolved={nextUnsolved ? () => onNavigate(nextUnsolved) : undefined}
            nextAction={nextAction}
          />
        );
      })()}
      {showLose && (
        <LoseModal
          swapsUsed={swapsUsed}
          maxSwaps={puzzle.maxSwaps}
          onKeepPlaying={() => { setShowLose(false); setShowToast(false); }}
          onRetry={puzzle.date !== "random" ? onRetry : undefined}
          onStats={onStats}
          onLeaderboard={onLeaderboard}
          nextAction={nextAction}
        />
      )}
    </>
  );
}

// --- Streak animation demo harness ---
// Opt-in via ?streakDemo=from:to (e.g. ?streakDemo=2:5). Renders the StreakToast
// on demand against the real header badge, so the once-per-day animation can be
// triggered and replayed instantly — for manual testing on any device and for
// automated (Playwright) positional checks. Exposes window.__replayStreak().
function parseStreakDemo(): { from: number; to: number } | null {
  const raw = new URLSearchParams(window.location.search).get("streakDemo");
  if (!raw) return null;
  const [from, to] = raw.split(":").map(Number);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return null;
  return { from, to };
}

function StreakDemo({ badgeRef }: { badgeRef: React.RefObject<HTMLButtonElement | null> }) {
  const demo = useMemo(parseStreakDemo, []);
  const [nonce, setNonce] = useState(0);
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!demo) return;
    const w = window as unknown as { __replayStreak?: () => void };
    w.__replayStreak = () => setNonce((n) => n + 1);
    // Wait until the header streak badge exists (puzzle loads async) before the
    // first play, so the toast anchors to the badge rather than a default spot.
    const id = setInterval(() => {
      if (badgeRef.current) { setArmed(true); clearInterval(id); }
    }, 50);
    return () => { clearInterval(id); delete w.__replayStreak; };
  }, [demo, badgeRef]);
  if (!demo || !armed) return null;
  return (
    <>
      <StreakToast key={nonce} from={demo.from} to={demo.to} badgeRef={badgeRef} />
      <button
        onClick={() => setNonce((n) => n + 1)}
        style={{ position: "fixed", bottom: 8, left: 8, zIndex: 400, fontSize: 12, opacity: 0.6 }}
      >
        replay streak
      </button>
    </>
  );
}

// --- App ---

export default function App() {
  useTheme();
  const streakBadgeRef = useRef<HTMLButtonElement>(null);

  const [initial] = useState(parseRoute);
  const [view, setView] = useState<View>(initial.view);
  const [puzzle, setPuzzle] = useState<Puzzle | null>(null);
  const [currentSize, setCurrentSize] = useState<GridSize>(initial.size);
  const [currentDate, setCurrentDate] = useState<string>(initial.date);
  const [puzzleKeyCounter, setPuzzleKeyCounter] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showChangelog, setShowChangelog] = useState(() => hasUnseenChangelog());

  const loadDailyPuzzle = useCallback(
    async (size: GridSize, date: string, replace = false) => {
      setCurrentSize(size);
      setCurrentDate(date);
      setView("game");
      setError(null);
      setLoading(true);

      const route: RouteState = { view: "game", size, date };
      if (replace) {
        replaceRoute(route);
      } else {
        pushRoute(route);
      }

      try {
        const p = await fetchPuzzle(size, date);
        setPuzzle(p);
        setPuzzleKeyCounter((k) => k + 1);
      } catch (err) {
        setError(`Failed to load puzzle. ${err instanceof Error ? err.message : "Please check your connection."}`);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const fetchNewRandom = useCallback(
    async (size: GridSize, difficulty?: Difficulty) => {
      setCurrentSize(size);
      setCurrentDate("random");
      setView("game");
      setError(null);
      setLoading(true);

      // Clear saved game state for this random slot so useGame starts fresh
      clearGameState(`random:${size}`);

      pushRoute({ view: "game", size, date: "random" });

      try {
        const p = await fetchRandomPuzzle(size, difficulty);
        saveRandomPuzzleToStorage(size, p);
        setPuzzle(p);
        setPuzzleKeyCounter((k) => k + 1);
      } catch (err) {
        setError(`Failed to generate puzzle. ${err instanceof Error ? err.message : "Please check your connection."}`);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const restoreOrFetchRandom = useCallback(
    async (size: GridSize) => {
      const saved = loadRandomPuzzleFromStorage(size);
      if (saved) {
        setCurrentSize(size);
        setCurrentDate("random");
        setView("game");
        setPuzzle(saved);
        setPuzzleKeyCounter((k) => k + 1);
        setLoading(false);
        replaceRoute({ view: "game", size, date: "random" });
        return;
      }
      await fetchNewRandom(size);
    },
    [fetchNewRandom]
  );

  // Load initial puzzle from URL
  useEffect(() => {
    if (initial.view === "game") {
      if (initial.date === "random") {
        restoreOrFetchRandom(initial.size);
      } else {
        loadDailyPuzzle(initial.size, initial.date, true);
      }
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle browser back/forward
  useEffect(() => {
    const handlePopState = () => {
      const route = parseRoute();
      if (route.view === "landing") {
        setView("landing");
      } else if (route.view === "privacy") {
        setView("privacy");
      } else if (route.view === "stats") {
        setView("stats");
        setCurrentSize(route.size);
      } else if (route.view === "leaderboard") {
        setView("leaderboard");
        setCurrentSize(route.size);
      } else if (route.view === "archive") {
        setView("archive");
        setCurrentSize(route.size);
      } else if (route.date === "random") {
        restoreOrFetchRandom(route.size);
      } else {
        loadDailyPuzzle(route.size, route.date, true);
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [loadDailyPuzzle, restoreOrFetchRandom]);

  const handleNewGame = useCallback(() => {
    fetchNewRandom(currentSize);
  }, [currentSize, fetchNewRandom]);

  const { markSolved, isSolved } = useLocalProgress();

  const handleRetry = useCallback(() => {
    if (currentDate === "random" || !puzzle) return;
    const key = puzzleKey(currentSize, currentDate);
    // If not already marked as solved (in-progress reset), mark as failed attempt
    if (!isSolved(currentDate, currentSize)) {
      markSolved(currentDate, currentSize, puzzle.maxSwaps + 1, puzzle.maxSwaps, 0);
    }
    // Clear game state for this puzzle
    clearGameState(key);
    // Reload the same puzzle (forces GameView remount with fresh state)
    setPuzzleKeyCounter((k) => k + 1);
  }, [currentDate, currentSize, puzzle, markSolved, isSolved]);

  const handleSizeChange = useCallback(
    (size: GridSize) => {
      if (size === currentSize) return;
      // For archive/stats/leaderboard, just update size and URL without leaving the view
      if (view === "archive" || view === "stats" || view === "leaderboard") {
        setCurrentSize(size);
        replaceRoute({ view, size, date: currentDate });
        return;
      }
      if (currentDate === "random") {
        fetchNewRandom(size);
      } else {
        loadDailyPuzzle(size, currentDate);
      }
    },
    [view, currentSize, currentDate, loadDailyPuzzle, fetchNewRandom]
  );

  const handleArchivePuzzle = useCallback(
    (date: string, size: GridSize) => {
      loadDailyPuzzle(size, date);
    },
    [loadDailyPuzzle]
  );

  const navigateToArchive = useCallback(() => {
    setView("archive");
    pushRoute({ view: "archive", size: currentSize, date: currentDate });
  }, [currentSize, currentDate]);

  const handlePlayFromLanding = useCallback(
    (size: GridSize) => {
      const today = getUtcToday();
      loadDailyPuzzle(size, today);
    },
    [loadDailyPuzzle]
  );

  const navigateToStats = useCallback(() => {
    setView("stats");
    pushRoute({ view: "stats", size: currentSize, date: currentDate });
  }, [currentSize, currentDate]);

  const navigateToLeaderboard = useCallback((params?: { view: string; date?: string }) => {
    setView("leaderboard");
    const qs = params ? `?view=${params.view}${params.date ? `&date=${params.date}` : ""}` : "";
    const url = `/${currentSize}/leaderboard${qs}`;
    if (window.location.pathname + window.location.search !== url) {
      window.history.pushState(null, "", url);
    }
  }, [currentSize]);

  const handleLeaderboard = useCallback(() => {
    navigateToLeaderboard(currentDate !== "random" ? { view: "time", date: currentDate } : undefined);
  }, [currentDate, navigateToLeaderboard]);

  const navigateToLanding = useCallback(() => {
    setView("landing");
    window.history.pushState(null, "", "/");
  }, []);

  const navigateToPrivacy = useCallback(() => {
    setView("privacy");
    window.history.pushState(null, "", "/privacy");
  }, []);

  const navigateToDaily = useCallback(() => {
    const today = getUtcToday();
    // Navigate to most recent unsolved puzzle, or today if all solved
    if (!isSolved(today, currentSize)) {
      loadDailyPuzzle(currentSize, today);
      return;
    }
    const unsolved = findUnsolved(today, -1, currentSize, isSolved);
    loadDailyPuzzle(currentSize, unsolved ?? today);
  }, [currentSize, loadDailyPuzzle, isSolved]);

  // Smart next action button for modals (must be before early returns)
  const nextAction = useMemo(() => {
    if (currentDate === "random") {
      return { label: "New Game", onClick: handleNewGame };
    }
    const otherSize: GridSize = currentSize === "7x7" ? "9x9" : "7x7";
    if (!isSolved(currentDate, otherSize)) {
      return {
        label: otherSize,
        onClick: () => loadDailyPuzzle(otherSize, currentDate),
      };
    }
    return { label: "Archive", onClick: navigateToArchive };
  }, [currentDate, currentSize, handleNewGame, isSolved, loadDailyPuzzle, navigateToArchive]);

  const handleNewGameWithDifficulty = useCallback(
    (difficulty: Difficulty) => fetchNewRandom(currentSize, difficulty),
    [currentSize, fetchNewRandom]
  );

  const headerProps = {
    date: currentDate,
    size: currentSize,
    difficulty: puzzle?.difficulty,
    streakBadgeRef,
    onSizeChange: handleSizeChange,
    onHowToPlay: navigateToLanding,
    onNewGame: handleNewGame,
    onNewGameWithDifficulty: handleNewGameWithDifficulty,
    onDaily: navigateToDaily,
    onArchive: navigateToArchive,
    onStats: navigateToStats,
    onLeaderboard: handleLeaderboard,
  };

  return (
    <>
      {view === "landing" && (
        <Landing onPlay={handlePlayFromLanding} />
      )}
      {view === "privacy" && (
        <Privacy onBack={() => { window.history.back(); }} />
      )}
      {view === "stats" && (
        <>
          <Header {...headerProps} showDate={false} />
          <Stats size={currentSize} onSelectPuzzle={handleArchivePuzzle} />
        </>
      )}
      {view === "leaderboard" && (
        <>
          <Header {...headerProps} showDate={false} />
          <Leaderboard size={currentSize} />
        </>
      )}
      {view === "archive" && (
        <>
          <Header {...headerProps} showDate={false} />
          <Archive
            onSelectPuzzle={handleArchivePuzzle}
            size={currentSize}
          />
        </>
      )}
      {view === "game" && (
        <>
          {error && (
            <div className="error-banner">{error}</div>
          )}
          {!loading && currentDate !== "random" && currentDate < getUtcToday() && (
            <div className="past-date-banner">
              You're viewing a past puzzle.{" "}
              <button onClick={() => loadDailyPuzzle(currentSize, getUtcToday())}>Play today's puzzle →</button>
            </div>
          )}
          {loading || !puzzle ? (
            <>
              <Header {...headerProps} />
              <main className="game-container">
                <LoadingGrid size={currentSize} />
              </main>
            </>
          ) : (
            <>
              <Header {...headerProps} />
              <GameView
                key={puzzleKeyCounter}
                puzzle={puzzle}
                onRetry={handleRetry}
                onMarkSolved={markSolved}
                onStats={navigateToStats}
                onLeaderboard={navigateToLeaderboard}
                nextAction={nextAction}
                onNavigate={(date) => loadDailyPuzzle(currentSize, date)}
                streakBadgeRef={streakBadgeRef}
              />
            </>
          )}
        </>
      )}
      {showChangelog && (
        <ChangeLog onClose={() => { markChangelogSeen(); setShowChangelog(false); }} />
      )}
      <StreakDemo badgeRef={streakBadgeRef} />
      <Footer onPrivacy={navigateToPrivacy} onChangelog={() => setShowChangelog(true)} />
    </>
  );
}
